import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useMemo } from 'react';
import type { SubLeaseFilter, SortConfig } from '../types/lease';
import type { SubLeaseSortField } from '../components/SubLeases/SubLeaseSortControls';

// Define a simple interface for sublease data
export interface DirectSublease {
  lease_id: string;
  lease_holder: string | null;
  holder_name: string | null;
  lease_start: string | null;
  lease_end: string | null;
  lease_method: string | null;
  lease_type: string | null;
  lease_furth_term: string | null;
  lease_rent_freq: string | null;
  lease_dealing: string | null;
  lease_deal_doc: string | null;
  // Add community and lot fields for display purposes
  community_name?: string;
  lot_id?: string;
  lot_avail?: boolean;
  lot_perm_use?: string;
  rental_amount?: number | null;
}

export interface DirectSubLeaseResponse {
  data: DirectSublease[] | null;
  error: Error | null;
  isLoading: boolean;
}

/**
 * Custom hook for fetching sublease data directly
 * This uses the same approach as the test-subleases.ts file
 */
export function useDirectSubLeases(
  filters: SubLeaseFilter,
  sortConfig: SortConfig,
  searchQuery: string
): DirectSubLeaseResponse {
  // Fetch the data
  const { data, error, isLoading } = useQuery({
    queryKey: ['directSubleases'],
    queryFn: async () => {
      try {
        console.log('Fetching sublease data directly...');
        
        // Use the same approach as the test page - simple query to subleases table
        const { data, error } = await supabase
          .from('subleases')
          .select(`
            lease_id,
            lease_holder,
            lease_start,
            lease_end,
            lease_method,
            lease_type,
            lease_furth_term,
            lease_rent_freq,
            lease_dealing,
            lease_deal_doc,
            leaseholders:lease_holder(holder_name)
          `)
          .order('lease_id', { ascending: true });

        if (error) {
          console.error('Error fetching sublease data:', error);
          throw error;
        }

        console.log('Raw data from Supabase:', data);

        // Transform the data to flatten the nested leaseholders object
        const transformedData = data?.map(item => {
          // Extract the holder_name from the nested leaseholders object
          const leaseholderData = item.leaseholders as unknown as { holder_name: string } | null;
          
          return {
            lease_id: item.lease_id,
            lease_holder: item.lease_holder,
            holder_name: leaseholderData?.holder_name || 'N/A',
            lease_start: item.lease_start,
            lease_end: item.lease_end,
            lease_method: item.lease_method,
            lease_type: item.lease_type,
            lease_furth_term: item.lease_furth_term,
            lease_rent_freq: item.lease_rent_freq,
            lease_dealing: item.lease_dealing,
            lease_deal_doc: item.lease_deal_doc
          };
        }) as DirectSublease[] | null;

        console.log('Transformed data:', transformedData);
        
        return transformedData || [];
      } catch (error) {
        console.error('Unexpected error fetching sublease data:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Apply filtering and sorting
  const filteredAndSortedData = useMemo(() => {
    if (!data) return null;

    console.log('Filtering and sorting data:', data);

    let filteredLeases = [...data];

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredLeases = filteredLeases.filter(lease => 
        (lease.lease_id?.toLowerCase() || '').includes(query) ||
        (lease.lease_dealing?.toLowerCase() || '').includes(query) ||
        (lease.holder_name?.toLowerCase() || '').includes(query)
      );
    }

    // Apply filters
    if (filters.holder) {
      filteredLeases = filteredLeases.filter(lease => 
        lease.holder_name === filters.holder
      );
    }

    if (filters.leaseDealingNo) {
      const dealingNo = filters.leaseDealingNo.toLowerCase();
      filteredLeases = filteredLeases.filter(lease => 
        lease.lease_dealing?.toLowerCase().includes(dealingNo)
      );
    }

    if (filters.endDateFrom) {
      const fromDate = new Date(filters.endDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      
      filteredLeases = filteredLeases.filter(lease => {
        if (!lease.lease_end) return false;
        
        const leaseEndDate = new Date(lease.lease_end);
        leaseEndDate.setHours(0, 0, 0, 0);
        
        return leaseEndDate >= fromDate;
      });
    }

    if (filters.endDateTo) {
      const toDate = new Date(filters.endDateTo);
      toDate.setHours(23, 59, 59, 999);
      
      filteredLeases = filteredLeases.filter(lease => {
        if (!lease.lease_end) return false;
        
        const leaseEndDate = new Date(lease.lease_end);
        
        return leaseEndDate <= toDate;
      });
    }

    // Apply sorting
    const sortedLeases = [...filteredLeases].sort((a, b) => {
      const field = sortConfig.field as SubLeaseSortField;
      const direction = sortConfig.direction === 'asc' ? 1 : -1;

      // Helper function to safely compare strings
      const compareStrings = (strA: string | null | undefined, strB: string | null | undefined) => {
        return direction * ((strA || '').localeCompare(strB || ''));
      };

      // Helper function to safely compare dates
      const compareDates = (dateA: string | null | undefined, dateB: string | null | undefined) => {
        if (!dateA) return direction;
        if (!dateB) return -direction;
        return direction * (new Date(dateA).getTime() - new Date(dateB).getTime());
      };

      switch (field) {
        case 'lease_dealing':
          return compareStrings(a.lease_dealing, b.lease_dealing);
        case 'holder':
          return compareStrings(a.holder_name, b.holder_name);
        case 'start_date':
          return compareDates(a.lease_start, b.lease_start);
        case 'end_date':
          return compareDates(a.lease_end, b.lease_end);
        case 'lease_method':
          return compareStrings(a.lease_method, b.lease_method);
        case 'lease_type':
          return compareStrings(a.lease_type, b.lease_type);
        case 'lease_furth_term':
          return compareStrings(a.lease_furth_term, b.lease_furth_term);
        case 'lease_rent_freq':
          return compareStrings(a.lease_rent_freq, b.lease_rent_freq);
        default:
          return 0;
      }
    });

    console.log('Filtered and sorted data:', sortedLeases);
    return sortedLeases;
  }, [data, filters, sortConfig, searchQuery]);

  return {
    data: filteredAndSortedData,
    error: error as Error | null,
    isLoading,
  };
}
